import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
// components
import { LogoOnlyLayout } from "./components/layout";
import { Loader } from "./components";
// constants
import { APP_ROUTES } from "./common/constants";

const ProductsLayout = lazy(() => import("./components/layout/ProductsLayout"));
const SettingsLayout = lazy(() => import("./components/layout/SettingsLayout"));
const OrdersLayout = lazy(() => import("./components/layout/OrdersLayout"));
const AddProductLayout = lazy(() => import("./components/layout/AddProductLayout"));

const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
// app pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AppPlan = lazy(() => import("./pages/AppPlan"));
// products
const ProductsPO = lazy(() => import("./pages/Products/ProductsPO"));
const ProductsBIS = lazy(() => import("./pages/Products/ProductsBIS"));
const ProductsCS = lazy(() => import("./pages/Products/ProductsCS"));
const ProductsIS = lazy(() => import("./pages/Products/ProductsIS"));
const AddProduct = lazy(() => import("./pages/Products/AddProduct"));
const ProductConfigContainer = lazy(() => import("./pages/Products/AddProduct/ProductConfigContainer"));
const ProductVariants = lazy(() => import("./pages/Products/ProductVariants"));
// settings
const Settings = lazy(() => import("./pages/Settings/Settings"));
const Templates = lazy(() => import("./pages/Settings/Templates"));
const Template = lazy(() => import("./pages/Settings/Template-new"));
const AdvancedSettings = lazy(() => import("./pages/Settings/AdvancedSettings"));
const SellingPlans = lazy(() => import("./pages/Settings/SellingPlans"));
const SellingPlan = lazy(() => import("./pages/Settings/SellingPlan"));
const Selectors = lazy(() => import("./pages/Settings/sections/Selectors"));
// notifications
const NotificationsPage = lazy(() => import("./pages/Notifications/NotificationsPage"));
const PreOrderNotificationEmail = lazy(() => import("./pages/Notifications/PreOrder/NotificationEmail"));
const PreOrderNotificationSms = lazy(() => import("./pages/Notifications/PreOrder/NotificationSms"));
const PreOrderNotificationShippingSms = lazy(() => import("./pages/Notifications/PreOrder/NotificationShipping"));
const BISNotificationEmail = lazy(() => import("./pages/Notifications/BIS/NotificationEmail"));
// orders
const PreOrderNotifications = lazy(() => import("./pages/Orders/Notifications"));
const PreOrders = lazy(() => import("./pages/Orders/PreOrders"));
const PreOrder = lazy(() => import("./pages/Orders/PreOrder"));
// account
const Account = lazy(() => import("./pages/Account/Account"));
// const AccountSubscribed = lazy(() => import("./pages/Account/AccountSubscribed"));
// bisNotifications
const BISNotifications = lazy(() => import("./pages/BISNotifications"));
// integrations
const Integrations = lazy(() => import("./pages/Integrations"));
// automation
const AutomationTags = lazy(() => import("./pages/Automations/Tags"));
const AutomationTag = lazy(() => import("./pages/Automations/Tags/Tag"));
// activity log
const ActivityLog = lazy(() => import("./pages/ActivityLog"));
// cancel order
const CancelOrder = lazy(() => import("./pages/CancelOrder"));

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path={APP_ROUTES.DASHBOARD} element={<AppPlan />}>
          <Route index path={APP_ROUTES.DASHBOARD} element={<Dashboard />} />
          <Route path={APP_ROUTES.PRODUCTS.ROOT} element={<ProductsLayout />}>
            <Route index element={<Navigate to={APP_ROUTES.PRODUCTS.PREORDERS} replace />} />

            <Route path={APP_ROUTES.PRODUCTS.PREORDERS} element={<ProductsPO />} />

            <Route path={APP_ROUTES.PRODUCTS.BACKINSTOCK} element={<ProductsBIS />} />

            <Route path={APP_ROUTES.PRODUCTS.COMINGSOON} element={<ProductsCS />} />

            <Route path={APP_ROUTES.PRODUCTS.INSTOCK} element={<ProductsIS />} />
          </Route>
          <Route path={APP_ROUTES.PRODUCTS.ADD.ROOT} element={<AddProductLayout />}>
            <Route index element={<AddProduct />} />

            <Route path={APP_ROUTES.PRODUCTS.ADD.PREORDERS} element={<ProductConfigContainer />} />
          </Route>
          <Route path={APP_ROUTES.ORDERS.ROOT} element={<OrdersLayout />}>
            <Route index element={<PreOrders />} />

            <Route path={APP_ROUTES.ORDERS.NOTIFICATIONS} element={<PreOrderNotifications />} />
          </Route>
          <Route path={`${APP_ROUTES.ORDERS.ROOT}/:id`} element={<PreOrder />} />
          <Route path={APP_ROUTES.PRODUCTS.VARIANT} element={<ProductVariants />} />
          <Route path={APP_ROUTES.SETTINGS.ROOT} element={<SettingsLayout />}>
            <Route index element={<Settings />} />

            <Route path={APP_ROUTES.SETTINGS.TEMPLATES} element={<Templates />} />

            <Route path={APP_ROUTES.SETTINGS.SELLING_PLANS} element={<SellingPlans />} />

            <Route path={APP_ROUTES.SETTINGS.ADVANCED.ROOT} element={<AdvancedSettings />}>
              <Route index element={<Navigate to={APP_ROUTES.SETTINGS.ADVANCED.SELECTORS} replace />} />

              <Route
                path={APP_ROUTES.SETTINGS.ADVANCED.SELECTORS}
                element={
                  <Suspense fallback={<Loader />}>
                    <Selectors />
                  </Suspense>
                }
              />
            </Route>
          </Route>
          <Route path={`${APP_ROUTES.SETTINGS.SELLING_PLANS}/:id`} element={<SellingPlan />} />
          <Route path={`${APP_ROUTES.SETTINGS.TEMPLATES}/:id/*`} element={<Template />} />

          <Route path={APP_ROUTES.NOTIFICATIONS.ROOT}>
            <Route index element={<NotificationsPage />} />
            <Route path={`${APP_ROUTES.NOTIFICATIONS.PREORDERS.EMAIL}/*`} element={<PreOrderNotificationEmail />} />
            <Route path={`${APP_ROUTES.NOTIFICATIONS.PREORDERS.SMS}/*`} element={<PreOrderNotificationSms />} />
            <Route
              path={`${APP_ROUTES.NOTIFICATIONS.PREORDERS.SHIPPING_EMAIL}/*`}
              element={<PreOrderNotificationShippingSms />}
            />
            <Route path={`${APP_ROUTES.NOTIFICATIONS.BACKINSTOCK.EMAIL}/*`} element={<BISNotificationEmail />} />
          </Route>
          <Route path={APP_ROUTES.AUTOMATIONS.ROOT}>
            <Route index element={<Navigate to={APP_ROUTES.AUTOMATIONS.TAGS} replace />} />

            <Route path={APP_ROUTES.AUTOMATIONS.TAGS} element={<AutomationTags />} />

            <Route path={`${APP_ROUTES.AUTOMATIONS.TAGS}/:name`} element={<AutomationTag />} />
          </Route>
          <Route path={APP_ROUTES.BACKINSTOCK} element={<BISNotifications />} />
          <Route path={APP_ROUTES.ACCOUNT.ROOT} element={<Account />} />
          {/* <Route path={APP_ROUTES.ACCOUNT.SUBSCRIBED} element={<AccountSubscribed />} /> */}
          <Route path={APP_ROUTES.INTEGRATIONS} element={<Integrations />} />
          <Route path={APP_ROUTES.ACTIVITY_LOG} element={<ActivityLog />} />
        </Route>

        <Route path={APP_ROUTES.CUSTOMERS} element={<LogoOnlyLayout disabledLink />}>
          <Route index element={<CancelOrder />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
